import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  detectUserTimezone, 
  detectUserLocation
} from '../../lib/services/timezone-location-service.js';
import Button from './ui/Button';
import Icon from './AppIcon';
import Input from './ui/Input';

const LocationOnboarding = ({ onComplete }) => {
  const { updateProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [timezone, setTimezone] = useState('');
  const [detectFailed, setDetectFailed] = useState(false);
  const [manualLocation, setManualLocation] = useState({ city: '', state: '', country: '' });

  const handleDetectLocation = async () => {
    setLoading(true);
    setDetectFailed(false);
    try {
      const location = await detectUserLocation();
      setLocationData(location);
      setTimezone(location.timezone);
      setStep(2);
    } catch (error) {
      console.error('Location detection failed:', error);
      setDetectFailed(true);
      setTimezone(detectUserTimezone());
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const { city, state, country } = manualLocation;
    if (!city?.trim() || !state?.trim()) {
      alert('Please enter your city and state.');
      return;
    }
    if (!country?.trim()) {
      alert('Please select your country.');
      return;
    }
    const tz = timezone || detectUserTimezone();
    setTimezone(tz);
    setLocationData({
      city: city.trim(),
      state: state.trim(),
      country: country.trim(),
      timezone: tz
    });
    setStep(2);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Save location if detected
      if (locationData) {
        await updateProfile({
          location_city: locationData.city,
          location_state: locationData.state,
          location_country: locationData.country,
          location_coordinates: locationData.coordinates
        });
      }

      // Save timezone and default schedule
      await updateProfile({
        timezone: timezone,
        sms_schedule_start: '09:00',
        sms_schedule_end: '17:00',
        sms_schedule_days: [1, 2, 3, 4, 5], // Monday-Friday
        sms_schedule_enabled: true,
        location_detection_enabled: !!locationData
      });

      onComplete?.();
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      alert('Failed to save settings. You can configure them later in settings.');
      onComplete?.();
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    const manualReady = manualLocation.city?.trim() && manualLocation.state?.trim() && manualLocation.country?.trim();

    return (
      <div className="max-w-md mx-auto text-center p-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="MapPin" size={32} className="text-primary" />
        </div>
        
        <h2 className="text-2xl font-bold text-foreground mb-2">Set Your Location</h2>
        <p className="text-muted-foreground mb-6">
          Required to send touchpoint messages at the right time and use the correct holiday calendar for your region.
        </p>
        
        {!detectFailed ? (
          <div className="space-y-3">
            <Button 
              onClick={handleDetectLocation}
              loading={loading}
              className="w-full"
            >
              <Icon name="Navigation" size={16} className="mr-2" />
              Auto-Detect My Location
            </Button>

            <div className="relative flex items-center py-1">
              <div className="flex-1 border-t border-border" />
              <span className="mx-3 text-xs text-muted-foreground">or enter manually</span>
              <div className="flex-1 border-t border-border" />
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="City"
                value={manualLocation.city}
                onChange={(e) => setManualLocation(prev => ({ ...prev, city: e.target.value }))}
                className="flex-1"
              />
              <Input
                placeholder="State / Province"
                value={manualLocation.state}
                onChange={(e) => setManualLocation(prev => ({ ...prev, state: e.target.value }))}
                className="flex-1"
              />
            </div>
            <select
              value={manualLocation.country}
              onChange={(e) => setManualLocation(prev => ({ ...prev, country: e.target.value }))}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select country…</option>
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </select>
            <Button 
              onClick={handleManualSubmit}
              variant="outline"
              className="w-full"
              disabled={!manualReady}
            >
              Use Manual Location
            </Button>
          </div>
        ) : (
          <div className="space-y-4 text-left">
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Auto-detect failed. Please enter your location to continue.
            </p>
            <Input
              label="City"
              placeholder="e.g. Toronto"
              value={manualLocation.city}
              onChange={(e) => setManualLocation(prev => ({ ...prev, city: e.target.value }))}
              required
            />
            <Input
              label="State / Province"
              placeholder="e.g. ON"
              value={manualLocation.state}
              onChange={(e) => setManualLocation(prev => ({ ...prev, state: e.target.value }))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Country <span className="text-destructive">*</span></label>
              <select
                value={manualLocation.country}
                onChange={(e) => setManualLocation(prev => ({ ...prev, country: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                required
              >
                <option value="">Select country…</option>
                <option value="CA">Canada</option>
                <option value="US">United States</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleDetectLocation} loading={loading} className="flex-1">
                Try Again
              </Button>
              <Button 
                onClick={handleManualSubmit}
                className="flex-1"
                disabled={!manualReady}
              >
                Continue
              </Button>
            </div>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground mt-4">
          Used for timezone scheduling and holiday-aware messaging. Not shared with third parties.
        </p>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="max-w-md mx-auto text-center p-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-foreground mb-2">Perfect!</h2>
        
        {locationData ? (
          <div className="text-left bg-card border border-border rounded-lg p-4 mb-6">
            <h3 className="font-medium text-foreground mb-2">Your Location:</h3>
            <p className="text-sm text-muted-foreground">
              {locationData.city}, {locationData.state} — {locationData.country === 'CA' ? 'Canada' : locationData.country === 'US' ? 'United States' : locationData.country}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Timezone: {timezone}
            </p>
            <button
              onClick={() => setStep(1)}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Not right? Go back and correct it
            </button>
          </div>
        ) : (
          <div className="text-left bg-card border border-border rounded-lg p-4 mb-6">
            <h3 className="font-medium text-foreground mb-2">Detected Timezone:</h3>
            <p className="text-sm text-muted-foreground">
              {timezone}
            </p>
          </div>
        )}
        
        <p className="text-muted-foreground mb-6">
          We've configured your SMS schedule to send messages during optimal business hours (9 AM - 5 PM, Monday-Friday) in your timezone for maximum client engagement.
        </p>
        
        <div className="space-y-3">
          <Button 
            onClick={handleComplete}
            loading={loading}
            className="w-full"
          >
            Continue
          </Button>
          
          <p className="text-xs text-muted-foreground">
            You can always adjust these settings later in your profile.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default LocationOnboarding;
